import { useContext, useState, createContext, useEffect } from "react";
import Cookies from "js-cookie";

import Auth from "../services/auth";
import { useCountdown } from "./useCountdown";

import toastMessage from "../utils/toastMessage";
import { decrypt, encrypt } from "../utils/encryptionHandler";
import { toast } from "react-toastify";


const authContext = createContext({
  onCredentialsSubmit: () => {},
  userLogin: "",
  passwordLogin: "",
  setUserLogin: () => {},
  setPasswordLogin: () => {},
  message: "",
  isLogin: false,
  isAuthenticated: false,
  handleLogout: () => {},
  storageDelete: () => {},
  twoFactorAuthData: {
    email: "",
    sms: "",
    hasSentContactType: false,
    selectedContactType: "email",
    hasExpirationCodeVerified: false,
  },
  handleSubmitContactType: (_, { setSubmitting }) => {},
  handleSecurityCodeSubmit: ({ values, setSubmitting }) => {},
  isVerificationCodeSubmitting: false,
  resendAmount: "",
  handleResendCodeVerification: (setSubmitting) => {},
  handleChangeTwoFactorData: (userData) => {},
  sessionIs: (userType) => Boolean,
  hasTwoFactorAuth: false,
  has2FASessionAccess: false,
  clearance: null,
});

export function AuthProvider({ children }) {
  const [userLogin, setUserLogin] = useState("");
  const [passwordLogin, setPasswordLogin] = useState("");
  const [isLogin, setIsLogin] = useState(false);
  const [message, setMessage] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasTwoFactorAuth, setHasTwoFactorAuth] = useState(false);
  const [userSession, setUserSession] = useState({});
  const [clearance, setClearance] = useState(null);

  const [twoFactorAuthData, setTwoFactorAuthData] = useState({
    email: "",
    sms: "",
    hasSentContactType: false,
    selectedContactType: "email",
    hasExpirationCodeVerified: false,
  });

  const [isVerificationCodeSubmitting, setIsVerificationCodeSubmitting] =
    useState(false);
  const [resendAmount, setResendAmount] = useState(2);

  const { handleCountdown } = useCountdown();

  function storageInsert(hashedCredentials, twoFactorAuthentication) {
    Cookies.set("isAuthenticated", true);
    Cookies.set("SESSION_EXT", hashedCredentials.password);
    Cookies.set("_ivc", hashedCredentials.iv);
    Cookies.set("hasTwoFactorAuth", twoFactorAuthentication);
  }

  function storageDelete() {
    Cookies.remove("isAuthenticated");
    Cookies.remove("hasTwoFactorAuth");
    Cookies.remove("SESSION_EXT");
    Cookies.remove("_ivc");
    Cookies.remove("clearance");
  }

  useEffect(() => {
    const userCredentials = Cookies.get("SESSION_EXT");
    const ivc = Cookies.get("_ivc");

    const cookieValue =
      Cookies.get("isAuthenticated") === undefined
        ? false
        : Cookies.get("isAuthenticated");
    const hasAuth = JSON.parse(cookieValue);

    const hasTwoFactorCookieValue =
      Cookies.get("hasTwoFactorAuth") === undefined
        ? false
        : Cookies.get("hasTwoFactorAuth");
    const hasTwoFactor = JSON.parse(hasTwoFactorCookieValue);

    if (!hasAuth) return;

    let descryptedCredentials = undefined;
    if (userCredentials && ivc) {
      descryptedCredentials = decrypt({ password: userCredentials, iv: ivc });
    }

    const [name, descryptedPw] = String(descryptedCredentials).split(":");

    const hasCookies =
      name !== undefined && descryptedPw !== undefined && hasAuth;

    if (hasCookies) {
      const clearanceCookie = Cookies.get("clearance");
      setIsAuthenticated(hasAuth);
      setHasTwoFactorAuth(hasTwoFactor);
      setClearance(clearanceCookie);
      handleLogin(name, descryptedPw, true, null, clearanceCookie);
    }
  }, []);

  async function handleLogin(name, descryptedPw, hasLogedWithCookies, token, clearanceToken) {
    const username = hasLogedWithCookies ? name : userLogin;
    const password = hasLogedWithCookies ? descryptedPw : passwordLogin;

    const hashedCredentials = encrypt(`${username}:${password}`);
    setIsLogin(true);

    await Auth.getClientIp();
    if (username && password) {
      try {
        const { loginResponse, sessionResponse } = await Auth.login(
          username,
          password,
          token,
          clearanceToken
        );

        const { ok, data } = loginResponse;

        if (!ok) {
          toastMessage.toastMessageError(loginResponse);
          return false;
        }

        const hasUserTwoFactorResponse = await getUserTwoFactorData(
          !!data.twoFactorAuthentication
        );

        if (!hasUserTwoFactorResponse) return;

        setUserSession(sessionResponse.ok && sessionResponse.data);
        storageInsert(hashedCredentials, !!data.twoFactorAuthentication);
        setUserLogin(username);
        setPasswordLogin(password);
        setMessage(data.message);
        if (data.clearance) {
          setClearance(data.clearance);
          Cookies.set("clearance", data.clearance);
        }
        setIsAuthenticated(true);
      } catch (error) {
        toastMessage.toastMessageError();
      } finally {
        setIsLogin(false);
      }
    }
  }

  function sessionIs(userType) {
    if (userSession.hasOwnProperty(userType)) return userSession[userType];

    return false;
  }

  const onCredentialsSubmit = async (token) => {
    return handleLogin(undefined, undefined, false, token);
  };

  const handleLogout = () => {
    storageDelete();
    setIsAuthenticated(false);
    setUserLogin("");
    setPasswordLogin("");
  };

  const handleSubmitContactType = async (_, { setSubmitting }) => {
    try {
      const formattedContactType =
        twoFactorAuthData.selectedContactType.toUpperCase();

      const { ok, data } = await Auth.sendContactType(formattedContactType);

      if (!ok)
        return toast.error(
          "Erro ao enviar o código de validação. Favor entrar em contato com o suporte de sua empresa.",
          {
            theme: "colored",
            closeButton: false,
          }
        );

      const sentContactStatus = {
        hasSentContactType: true,
      };

      handleChangeTwoFactorData(sentContactStatus);
      handleCountdown(data.tempoExpiracaoEmSegundos);

      toastMessage.toastMessageSuccess(
        `Código enviado: Por favor, verifique seu ${twoFactorAuthData.selectedContactType} para inserir o código.`
      );
    } catch (err) {
      toastMessage.toastMessageError();
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCodeVerification = async (setSubmitting) => {
    try {
      setSubmitting(true);

      const formattedContactType =
        twoFactorAuthData.selectedContactType.toUpperCase();

      const { ok, data } = await Auth.sendContactType(formattedContactType);

      if (!ok)
        return toast.error(
          "Erro ao enviar o código de validação. Favor entrar em contato com o suporte de sua empresa.",
          {
            theme: "colored",
            closeButton: false,
          }
        );

      handleCountdown(data.tempoExpiracaoEmSegundos);
      handleResendAmount();

      toastMessage.toastMessageSuccess(
        `Enviamos a você um novo código de verificação por ${
          twoFactorAuthData.selectedContactType
        }. Você possui ${resendAmount - 1} reenvio restante.`,
        {
          variant: "success",
        }
      );
    } catch (err) {
      toastMessage.toastMessageError();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecurityCodeSubmit = async ({ values, setSubmitting }) => {
    const formattedCode = Object.values(values).join("");

    try {
      setSubmitting(true);
      setIsVerificationCodeSubmitting(true);

      const response = await Auth.sendVerificationCode(formattedCode);

      if (!response.ok)
        return toast.error("Código de verificação inválido.", {
          theme: "colored",
          closeButton: false,
        });

      handleChangeTwoFactorData({
        hasExpirationCodeVerified: true,
      });
    } catch (err) {
      toastMessage.toastMessageError();
    } finally {
      setSubmitting(false);
      setIsVerificationCodeSubmitting(false);
    }
  };

  const handleResendAmount = () => {
    setResendAmount((prevAmount) => prevAmount - 1);
  };

  function handleChangeTwoFactorData(userData) {
    setTwoFactorAuthData((prevData) => {
      return {
        ...prevData,
        ...userData,
      };
    });
  }

  const getUserTwoFactorData = async (twoFactorAuthentication) => {
    try {
      const { ok, data } = await Auth.getUserTwoFactorData();

      if (!ok) {
        toast.error(
          "Erro ao enviar o código de validação. Favor entrar em contato com o suporte de sua empresa.",
          {
            theme: "colored",
            closeButton: false,
          }
        );

        return false;
      }

      const userContacts = {
        email: data.email,
        sms: data.telefone,
        selectedContactType: !!data.email
          ? "email"
          : !!data.telefone
          ? "sms"
          : "email",
      };

      setHasTwoFactorAuth(twoFactorAuthentication);
      handleChangeTwoFactorData(userContacts);
      return true;
    } catch (err) {
      toastMessage.toastMessageError();
    }
  };

  const has2FASessionAccess = sessionIs("banco") || sessionIs("rh");

  return (
    <authContext.Provider
      value={{
        onCredentialsSubmit,
        userLogin,
        passwordLogin,
        setUserLogin,
        setPasswordLogin,
        message,
        isLogin,
        isAuthenticated,
        handleLogout,
        storageDelete,
        twoFactorAuthData,
        handleSubmitContactType,
        handleSecurityCodeSubmit,
        isVerificationCodeSubmitting,
        resendAmount,
        handleResendCodeVerification,
        handleChangeTwoFactorData,
        hasTwoFactorAuth,
        sessionIs,
        has2FASessionAccess,
        clearance,
      }}
    >
      {children}
    </authContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(authContext);

  return context;
}
